import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ConnectionStatus } from '@capacitor/network';
import { ActivityLocationFacade } from '@curbnturf/activity/src/lib/+state/activity.facade';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { MapIconsComponent } from '@curbnturf/custom-svgs/src/lib/map-icons/map-icons.component';
import {
  ActivityLocation,
  Boondock,
  HelperFunctions,
  IActivityLocation,
  IBoondock,
  IListing,
  Listing,
  User,
} from '@curbnturf/entities';
import { siteComplete } from '@curbnturf/helpers';
import { ListingFacade } from '@curbnturf/listing';
import { Logger } from '@curbnturf/network';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, LoadingController, ModalController } from '@ionic/angular';
import { IonInfiniteScrollCustomEvent } from '@ionic/core';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddListingModalComponent } from './add-listing-modal/add-listing-modal.component';
import { BoondockListItemComponent } from './boondock-list-item/boondock-list-item.component';
import { ListingListItemComponent } from './listing-list-item/listing-list-item.component';
import { PoiListItemComponent } from './poi-list-item/poi-list-item.component';
import debounce = HelperFunctions.debounce;

@Component({
  selector: 'curbnturf-listing-list',
  templateUrl: './listing-list.component.html',
  styleUrls: ['./listing-list.component.scss'],
  standalone: true,
  imports: [
    BoondockListItemComponent,
    IonicModule,
    ListingListItemComponent,
    MapIconsComponent,
    NgClass,
    NgFor,
    NgIf,
    PoiListItemComponent,
    RouterLink,
  ],
})
export class ListingListComponent implements OnInit, OnDestroy {
  infinateScrollEvent: IonInfiniteScrollCustomEvent<string>;

  listingsComplete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  boondocksComplete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  poisComplete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  listings: Listing[] = [];
  boondocks: Boondock[] = [];
  pois: ActivityLocation[] = [];

  limitedListings: Listing[] = [];
  limitedBoondocks: Boondock[] = [];
  limitedPois: ActivityLocation[] = [];

  currentUser: User;

  connectionStatus?: ConnectionStatus;
  loading: HTMLIonLoadingElement;

  showListings: boolean = true;
  showBoondock: boolean = true;
  showPoi: boolean = true;

  subscriptions: Subscription[] = [];
  lastUpdated: DateTime;

  loadListingsDebounced: () => Promise<any>;

  get profileMessages(): string[] {
    if (this.currentUser) {
      const userCompletion = siteComplete.userProfileCompleted(this.currentUser);
      if (!userCompletion) {
        const userProfile = siteComplete.userProfilePercentComplete(this.currentUser);
        return userProfile.messages;
      }
    }
    return [];
  }

  constructor(
    private activityFacade: ActivityLocationFacade,
    private alertFacade: AlertFacade,
    private cd: ChangeDetectorRef,
    private loadingController: LoadingController,
    private logger: Logger,
    private modalController: ModalController,
    private listingFacade: ListingFacade,
    private boondockFacade: BoondockFacade,
    private userFacade: UserFacade,
  ) {
    this.loadListingsDebounced = debounce(() => {
      return this.loadListings();
    }, 500);
  }

  async ngOnInit() {
    this.subscriptions.push(
      this.userFacade.currentUserOnce$.subscribe(async (user) => {
        this.currentUser = user;
        await this.loadListingsDebounced();
      }),
      combineLatest([this.boondocksComplete$, this.listingsComplete$, this.poisComplete$]).subscribe(
        async ([boondocksComplete, listingsComplete, poisComplete]) => {
          if (boondocksComplete && listingsComplete && poisComplete && this.loading) {
            await this.loading.dismiss();
          }
        },
      ),
      this.listingFacade.currentUserListings$.pipe(map((listings) => this.updateListingsUser(listings))).subscribe({
        next: (listings: Listing[]) => {
          this.listings = [];
          this.listings.push(...listings);
          this.sortAll();
          this.listingsComplete$.next(true);
        },
        error: (err) => {
          this.logger.error('Failed to Retrieve Listings', { err });
          this.alertFacade.display({
            title: 'Failed to Retrieve Listings',
            body: 'An error occurred while retrieving site records: ' + err.message ? err.message : err,
            level: 'error',
          });
          this.listingsComplete$.next(true);
        },
      }),
      this.boondockFacade.currentUserBoondocks$
        .pipe(map((boondockListings) => this.updateBoondocksUser(boondockListings)))
        .subscribe({
          next: (boondocks) => {
            this.boondocks = [];
            this.boondocks.push(...boondocks);
            this.sortAll();
            this.boondocksComplete$.next(true);
          },
          error: (err) => {
            this.logger.error('Failed to Retrieve Boondocks', { err });
            this.alertFacade.display({
              title: 'Failed to Retrieve Boondocks',
              body: 'An error occurred while retrieving Boondock records: ' + err.message ? err.message : err,
              level: 'error',
            });
            this.boondocksComplete$.next(true);
          },
        }),
      this.activityFacade.currentUserActivities$
        .pipe(map((poiListings) => this.updatePoisUser(poiListings)))
        .subscribe({
          next: (pois) => {
            this.pois = [];
            this.pois.push(...pois);
            this.sortAll();
            this.poisComplete$.next(true);
          },
          error: (err) => {
            this.logger.error('Failed to Retrieve POIs', { err });
            this.alertFacade.display({
              title: 'Failed to Retrieve POIs',
              body: 'An error occurred while retrieving POI records: ' + err.message ? err.message : err,
              level: 'error',
            });
            this.poisComplete$.next(true);
          },
        }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async onAddListing() {
    const modal = await this.modalController.create({
      component: AddListingModalComponent,
      componentProps: {
        listings: this.listings,
        boondocks: this.boondocks,
      },
      cssClass: 'select-add-listing-modal',
    });
    await modal.present();
  }

  toggleHost() {
    this.showListings = !this.showListings;
    this.sortAll();
  }

  toggleBoondock() {
    this.showBoondock = !this.showBoondock;
    this.sortAll();
  }

  togglePoi() {
    this.showPoi = !this.showPoi;
    this.sortAll();
  }

  trackByString(index: number, listing: ActivityLocation | Listing | Boondock) {
    return listing.id || listing.syncId;
  }

  public async loadListings(): Promise<void> {
    this.clearData();
    this.loading = await this.loadingController.create({
      message: 'Loading Your Properties...',
      duration: 15000,
      backdropDismiss: true,
    });

    await this.loading.present();

    if (this.currentUser && this.currentUser.id) {
      this.listingsComplete$.next(false);
      this.listingFacade.loadAllListings({ userId: this.currentUser.id });

      this.poisComplete$.next(false);
      this.activityFacade.loadForUser();

      this.boondocksComplete$.next(false);
      this.boondockFacade.loadUserBoondocks(this.currentUser.id);
    }
    this.lastUpdated = DateTime.now();
  }

  private sortAll() {
    setTimeout(() => {
      // Listings are first sorted by type: Standard, Boondock, POI. Then they are sorted by creation date.
      this.listings.sort((a: Listing, b: Listing) => {
        if (a.created === b.created) {
          return 0;
        }
        return (a.created || 0) < (b.created || 0) ? 1 : -1;
      });

      this.boondocks.sort((a: Boondock, b: Boondock) => {
        if (a.created === b.created) {
          return 0;
        }
        return (a.created || 0) < (b.created || 0) ? 1 : -1;
      });

      this.pois.sort((a: ActivityLocation, b: ActivityLocation) => {
        if (a.created === b.created) {
          return 0;
        }
        return (a.created || 0) < (b.created || 0) ? 1 : -1;
      });

      this.limitedListings = [];
      this.limitedBoondocks = [];
      this.limitedPois = [];

      if (this.infinateScrollEvent?.target?.disabled) {
        this.infinateScrollEvent.target.disabled = false;
      }

      setTimeout(() => this.loadData());
    });
  }

  async loadData(event?: any) {
    const start = this.limitedListings.length + this.limitedBoondocks.length + this.limitedPois.length;
    const end = start + 10;

    if (this.showListings && start < this.listings.length) {
      this.limitedListings.push(...this.listings.slice(start, end));
    }

    if (
      this.showBoondock &&
      this.limitedListings.length < end &&
      this.limitedBoondocks.length < this.boondocks.length
    ) {
      const boondockStart = this.limitedBoondocks.length;
      const boondockEnd = end - this.limitedListings.length;
      this.limitedBoondocks.push(...this.boondocks.slice(boondockStart, boondockEnd));
    }

    if (
      this.showPoi &&
      this.limitedBoondocks.length + this.limitedListings.length < end &&
      this.limitedPois.length < this.pois.length
    ) {
      const poiStart = this.limitedPois.length;
      const poiEnd = end - this.limitedListings.length - this.limitedBoondocks.length;
      this.limitedPois.push(...this.pois.slice(poiStart, poiEnd));
    }

    this.cd.detectChanges();
    event?.target?.complete();

    if (
      this.limitedListings.length >= this.listings.length &&
      this.limitedBoondocks.length >= this.boondocks.length &&
      this.limitedPois.length >= this.pois.length &&
      event?.target
    ) {
      this.infinateScrollEvent = event;
      event.target.disabled = true;
    }
  }

  clearData() {
    this.limitedListings = [];
    this.limitedBoondocks = [];
    this.limitedPois = [];
    this.listings = [];
    this.boondocks = [];
    this.pois = [];
    this.listingsComplete$.next(false);
    this.boondocksComplete$.next(false);
    this.poisComplete$.next(false);
  }

  private updateBoondocksUser(boondocks: Boondock[] | IBoondock[]): Boondock[] {
    return boondocks.map((boondock) => {
      if (this.currentUser) {
        return new Boondock({ ...boondock, user: this.currentUser });
      }

      return new Boondock({ ...boondock });
    });
  }

  private updateListingsUser(listings: Listing[] | IListing[]): Listing[] {
    return listings.map((listing) => {
      if (this.currentUser) {
        return new Listing({ ...listing, user: this.currentUser });
      }

      return new Listing({ ...listing });
    });
  }

  private updatePoisUser(pois: IActivityLocation[]): ActivityLocation[] {
    return pois.map((poi) => {
      if (this.currentUser) {
        return new ActivityLocation({ ...poi, user: this.currentUser });
      }

      return new ActivityLocation({ ...poi });
    });
  }

  onListingUpdated(listing: Listing) {
    const foundIndex = this.listings.findIndex((el) => (listing.id = el.id));
    if (foundIndex !== -1) {
      this.listings.splice(foundIndex, 1, listing);
    } else {
      this.listings.push(listing);
    }
    this.sortAll();
  }
}
